/**
 * Admin authentication — US-A01.
 *
 * Two gates, in order:
 *   1. Supabase auth (the same auth.users table the mobile app uses).
 *   2. An `admin_users` row that is `is_active`.
 *
 * AC2 — "given student credentials, when I try to log in to admin, then access
 * is refused" — is the second gate. A student's password is valid; their
 * session is immediately discarded because there is no admin_users row.
 * RLS enforces the same thing server-side (docs/03 §4.3, `is_admin()`), so a
 * hand-crafted request gets nowhere either.
 */
import type { AuthProvider } from '@refinedev/core'
import { supabaseClient } from '@/lib/supabase'
import { t } from '@/providers/i18n-provider'
import type { AdminRole } from '@/types'

export interface AdminIdentity {
  id: string
  email: string
  name: string
  role: AdminRole
}

/**
 * Cached for the lifetime of the tab so `useCan()` — which fires on every
 * button render — does not hit the network. Cleared on logout and on any
 * auth error.
 */
let identityCache: AdminIdentity | null = null

async function loadAdminIdentity(): Promise<AdminIdentity | null> {
  if (identityCache) return identityCache

  const { data: sessionData } = await supabaseClient.auth.getSession()
  const user = sessionData.session?.user
  if (!user) return null

  const { data, error } = await supabaseClient
    .from('admin_users')
    .select('id, role, is_active')
    .eq('id', user.id)
    .maybeSingle()

  if (error || !data || !data.is_active) return null

  identityCache = {
    id: user.id,
    email: user.email ?? '',
    name: (user.user_metadata?.full_name as string | undefined) ?? user.email ?? '',
    role: data.role as AdminRole,
  }
  return identityCache
}

export function clearIdentityCache(): void {
  identityCache = null
}

/** Injected into the access-control provider. */
export async function getAdminRole(): Promise<AdminRole | undefined> {
  const identity = await loadAdminIdentity()
  return identity?.role
}

export const authProvider: AuthProvider = {
  async login({ email, password }: { email: string; password: string }) {
    clearIdentityCache()

    const { data, error } = await supabaseClient.auth.signInWithPassword({ email, password })
    if (error || !data.session) {
      return {
        success: false,
        error: {
          name: 'LoginError',
          message: error?.message ?? 'Identifiants invalides',
        },
      }
    }

    // Gate 2 — US-A01 AC2. A valid student session is not an admin session.
    const identity = await loadAdminIdentity()
    if (!identity) {
      await supabaseClient.auth.signOut()
      clearIdentityCache()
      return {
        success: false,
        error: {
          name: 'NotAnAdministrator',
          message: t('admin.login.notAnAdministrator'),
        },
      }
    }

    return { success: true, redirectTo: '/' }
  },

  async logout() {
    await supabaseClient.auth.signOut()
    clearIdentityCache()
    return { success: true, redirectTo: '/login' }
  },

  async check() {
    const identity = await loadAdminIdentity()
    if (identity) return { authenticated: true }
    // A signed-in non-admin is signed out rather than left in limbo.
    const { data } = await supabaseClient.auth.getSession()
    if (data.session) await supabaseClient.auth.signOut()
    return {
      authenticated: false,
      redirectTo: '/login',
      logout: true,
      error: { name: 'Unauthenticated', message: 'Session requise' },
    }
  },

  async onError(error) {
    // 401/403 from PostgREST means the session died or RLS refused. RLS is the
    // real boundary (docs/03 §10), so we trust its verdict over our cache.
    const status = (error as { statusCode?: number; status?: number })?.statusCode ?? undefined
    if (status === 401) {
      clearIdentityCache()
      return { logout: true, redirectTo: '/login', error }
    }
    return { error }
  },

  async getPermissions() {
    const identity = await loadAdminIdentity()
    return identity?.role ?? null
  },

  async getIdentity() {
    return await loadAdminIdentity()
  },

  async forgotPassword({ email }: { email: string }) {
    const { error } = await supabaseClient.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/login`,
    })
    return error
      ? { success: false, error: { name: 'ForgotPasswordError', message: error.message } }
      : { success: true }
  },
}
